import { datadogRum } from '@datadog/browser-rum';
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import useGetFeatureFlag from 'hooks/useGetFeatureFlag';
import { UserContext } from 'providers/UserProvider';

type DatadogRUMContextType = {
  isDatadogRumActive: boolean;
};

interface DatadogRUMProviderProps {
  name: string;
  children?: React.ReactNode;
}

const DatadogRUMContext = createContext<DatadogRUMContextType>({
  isDatadogRumActive: false,
});

const DatadogRUMProvider: React.FC<DatadogRUMProviderProps> = ({
  name,
  children,
}) => {
  const { interviewUid } = useParams();
  const { data } = useGetFeatureFlag(name, interviewUid);
  const { userProfile } = useContext(UserContext);
  const contextValue = useMemo(
    () => ({
      isDatadogRumActive: !!data?.is_active,
    }),
    [data?.is_active]
  );

  useEffect(() => {
    if (data?.is_active) {
      datadogRum.init({
        applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID || '',
        clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN || '',
        site: process.env.REACT_APP_DATADOG_SITE || '',
        service: process.env.REACT_APP_DATADOG_SERVICE || '',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackUserInteractions: true,
        trackResources: true,
        defaultPrivacyLevel: 'mask-user-input',
      });
      datadogRum.setUser({
        email: userProfile.email,
      });
    }
  }, [data?.is_active, userProfile.email]);

  return (
    <DatadogRUMContext.Provider value={contextValue}>
      {children}
    </DatadogRUMContext.Provider>
  );
};

export default DatadogRUMProvider;
export { DatadogRUMContext };
